import type { Currency } from '@ps/web-core-enums';
import { Size } from '@ps/web-core-enums';
import { useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { CasinoGameList, CasinoSliderButtons, useWidgetAnchor } from '@swf/casino';
import type { SliderHandles, State as SliderState } from '@swf/components/Slider/Slider';
import useCurrencyFormatter from '@swf/core/hooks/useCurrencyFormatter';
import { select } from '@swf/core/store';
import type { ShowField } from '@swf/core/types/CasinoGameList';
import type { WidgetFC } from '@swf/core/types/Widget';
import { getStyle, isVisibleByAuth } from '@swf/core/utils/casino';

import {
  TXTCLI_Jackpot_King,
  TXTCLI_Regal_pot,
  TXTCLI_Royal_pot,
  TXTCLI_win_up_to,
  TXTWCL_Paid_today,
  TXTWCL_Won_today
} from './CasinoJackpotWidget.messages.json';
import styles from './CasinoJackpotWidget.scss';
import jackpotBadge from './assets/jackpot-badge.png';
import jackpotKingLogo from './assets/jackpot-king-logo.png';
import JackpotInfoBlock from './components/JackpotInfoBlock';
import type { CasinoJackpotWidgetState } from './model';
import { jackpotWidgetModel, useJackpotWidgetModel } from './model';
import { adjustFontSizeToFit, getMaxFontSize, plainText } from './utils';

export const enum WidgetVariant {
  king = 'king',
  hunt = 'hunt'
}

const CasinoJackpotWidget: WidgetFC = () => {
  const { widgetSelect, widgetConfig } = useJackpotWidgetModel();
  const widgetState: CasinoJackpotWidgetState = useSelector(widgetSelect.get);
  const sliderRef = React.createRef<SliderHandles>();
  const containerRef = React.createRef<HTMLDivElement>();
  const [sliderState, setSliderState] = useState<SliderState>({ left: false, right: false });
  const { greaterThan } = useScreenSize();
  const isDesktop = greaterThan(Size.Large);
  const variantHunt = widgetConfig.widget_variant === WidgetVariant.hunt;
  const variantKing = widgetConfig.widget_variant === WidgetVariant.king;

  const {
    layout,
    render_info: renderInfo,
    featured_tab: featuredTab,
    params,
    description,
    hide_on_login: hideOnLogin,
    logged_in_only: loggedInOnly
  } = widgetConfig;
  const {
    winUpTo,
    wonToday,
    paidToday,
    jackpotKing,
    regalPot,
    royalPot,
    currency,
    gameConfigs: games
  } = widgetState;

  const widgetTargetId = useWidgetAnchor();
  const casinoGameListProps = {
    params,
    games,
    renderInfo: renderInfo as ShowField[],
    loading: false
  };

  const amountFirstBlock = useCurrencyFormatter()({
    value: (variantKing ? jackpotKing?.value : winUpTo) || 0,
    currency: variantKing ? jackpotKing?.currency : (currency as Currency),
    ...(variantKing && { cents: true })
  });

  const amountSecondBlock = useCurrencyFormatter()({
    value: (variantKing ? regalPot?.value : paidToday) || 0,
    currency: variantKing ? regalPot?.currency : (currency as Currency),
    ...(variantKing && { cents: true })
  });

  const amountThirdBlock = variantKing
    ? useCurrencyFormatter()({
        value: royalPot?.value || 0,
        currency: royalPot?.currency,
        cents: true
      })
    : plainText(wonToday || 0);

  // Refs for each JackpotInfoBlock
  const winUpToRef = useRef<HTMLParagraphElement>(null);
  const paidTodayRef = useRef<HTMLParagraphElement>(null);
  const wonTodayRef = useRef<HTMLParagraphElement>(null);
  const maxFontSize = getMaxFontSize();
  const [fontSize, setFontSize] = useState<number>(maxFontSize);

  const adjustFontSize = useCallback(() => {
    const elements = [winUpToRef.current, paidTodayRef.current, wonTodayRef.current].filter(
      (el) => el !== null
    ) as HTMLElement[];
    const maxFontSize = getMaxFontSize();

    if (elements.length) {
      const calculatedFontSize = adjustFontSizeToFit(elements, maxFontSize);
      setFontSize(calculatedFontSize);
    }
  }, [winUpToRef, paidTodayRef, wonTodayRef]);

  useEffect(() => {
    adjustFontSize();
  }, [amountFirstBlock, amountSecondBlock, amountThirdBlock, adjustFontSize]);

  useEffect(() => {
    window.addEventListener('resize', adjustFontSize);
    return () => {
      window.removeEventListener('resize', adjustFontSize);
    };
  }, [adjustFontSize]);

  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const shouldRender =
    games.length > 0 && isVisibleByAuth(loggedInOnly, hideOnLogin, isAuthenticated);

  if (!shouldRender) {
    return null;
  }

  return (
    <section
      className={styles.container}
      style={getStyle(layout)}
      ref={containerRef}
      id={widgetTargetId}
    >
      <div
        className={classNames(styles.wrapper, {
          [styles.king]: variantKing,
          [styles.hunt]: variantHunt
        })}
      >
        <div
          className={classNames(styles.tabsWrap, {
            [styles.king]: variantKing,
            [styles.hunt]: variantHunt
          })}
        >
          <div
            className={classNames(styles.firstBlockContainer, {
              [styles.active]: featuredTab === 'win'
            })}
          >
            <JackpotInfoBlock
              ref={winUpToRef}
              title={variantKing ? TXTCLI_Jackpot_King : TXTCLI_win_up_to}
              amount={amountFirstBlock}
              fontSize={fontSize}
            />
          </div>
          <img
            className={classNames({
              [styles.imgKing]: variantKing,
              [styles.imgHunt]: variantHunt
            })}
            src={variantKing ? jackpotKingLogo : jackpotBadge}
            alt="Jackpot Badge"
          />
          <div
            className={classNames(styles.infoBlockContainer, {
              [styles.king]: variantKing,
              [styles.hunt]: variantHunt
            })}
          >
            <JackpotInfoBlock
              title={variantKing ? TXTCLI_Jackpot_King : TXTCLI_win_up_to}
              amount={amountFirstBlock}
              fontSize={fontSize}
            />
            <JackpotInfoBlock
              className={classNames({
                [styles.withBorder]: featuredTab === 'paid'
              })}
              ref={paidTodayRef}
              title={variantKing ? TXTCLI_Regal_pot : TXTWCL_Paid_today}
              amount={amountSecondBlock}
              fontSize={fontSize}
            />
            <JackpotInfoBlock
              className={classNames({
                [styles.withBorder]: featuredTab === 'won'
              })}
              ref={wonTodayRef}
              title={variantKing ? TXTCLI_Royal_pot : TXTWCL_Won_today}
              amount={amountThirdBlock}
              fontSize={fontSize}
            />
          </div>
        </div>
        <div className={styles.headerButtons}>
          {isDesktop && (
            <div className={styles.sliderButtons}>
              <CasinoSliderButtons sliderRef={sliderRef} sliderState={sliderState} />
            </div>
          )}
        </div>
        <div className={styles.sliderContainer}>
          <CasinoGameList
            jackpotWidget
            rows={1}
            icon="icnSquare"
            onScroll={setSliderState}
            sliderRef={sliderRef}
            overlayHierarchy="jackpot_exclusive"
            {...casinoGameListProps}
          />
        </div>
        <div className={classNames(styles.description, styles.descriptionMobile)}>
          <span>{description}</span>
        </div>
      </div>
      <div className={classNames(styles.description, styles.descriptionDesktop)}>
        <span>{description}</span>
      </div>
    </section>
  );
};
CasinoJackpotWidget.model = jackpotWidgetModel;
export default CasinoJackpotWidget;
